/**
 * CommunityPlaybooksManagement (DEV-1277) — the org's playbook GOVERNANCE
 * surface, rendered as the "Playbooks" sub-tab of the community Management
 * tab. Distinct from the top-level consumer "Playbooks" catalog tab (adopt
 * active playbooks); this is where an author manages everything the org has
 * published — including HIDDEN playbooks, which never appear in the catalog.
 *
 * Resolves the navigation gap: hiding a playbook removes its catalog card
 * (and that card's "View master" link), which previously stranded the author
 * with no way back. Here every playbook — listed or hidden — is reachable,
 * with View master + Hide / Restore inline.
 *
 * Write actions (Hide/Restore) are gated to admins; the backend re-checks
 * via CanAuthorPlaybook. Read is allowed for any Management-tab viewer.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { useApiSecurityData } from "../../api/useApiSecurityData";
import {
  fetchMiniProjectPlaybooks,
  updateMiniProjectPlaybook,
  type MiniProjectPlaybook,
} from "../../api/miniProjects";
import styles from "./CommunityPlaybooksManagement.module.css";

interface Props {
  communityGuid: string;
  isAdmin: boolean;
}

export default function CommunityPlaybooksManagement({ communityGuid, isAdmin }: Props) {
  const { t } = useAppTranslation("pages");
  const navigate = useNavigate();
  const { securityUsers } = useApiSecurityData();
  const [playbooks, setPlaybooks] = useState<MiniProjectPlaybook[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyGuid, setBusyGuid] = useState<string | null>(null);
  const [confirmHideGuid, setConfirmHideGuid] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    // includeInactive: this is the management view — show hidden ones too.
    fetchMiniProjectPlaybooks({ ownerCommunityGuid: communityGuid, includeInactive: true })
      .then(setPlaybooks)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [communityGuid]);

  useEffect(() => {
    setPlaybooks(null);
    load();
  }, [load]);

  const setActive = useCallback(async (pb: MiniProjectPlaybook, active: boolean) => {
    setBusyGuid(pb.playbookGuid);
    setError(null);
    try {
      const updated = await updateMiniProjectPlaybook(pb.playbookGuid, { isActive: active });
      setPlaybooks((prev) =>
        (prev || []).map((p) => (p.playbookGuid === updated.playbookGuid ? updated : p)),
      );
      setConfirmHideGuid(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyGuid(null);
    }
  }, []);

  // Resolve author display name from the user GUID on each playbook.
  const authorByGuid = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of securityUsers || []) {
      const g = String(u.user_guid || "").toUpperCase();
      const name = String(u.display_name || "").trim();
      if (g && name) m.set(g, name);
    }
    return m;
  }, [securityUsers]);
  const authorName = useCallback(
    (guid?: string | null) => (guid ? authorByGuid.get(guid.toUpperCase()) || "" : ""),
    [authorByGuid],
  );

  // Listed first, then hidden; alphabetical within each group.
  const sorted = useMemo(
    () =>
      (playbooks || []).slice().sort((a, b) => {
        if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
        return a.title.localeCompare(b.title);
      }),
    [playbooks],
  );

  return (
    <div className={styles.wrap}>
      <p className={styles.intro}>{t("playbooksManage.intro")}</p>

      {error && <div className={styles.error}>{error}</div>}

      {!playbooks && !error && <div className={styles.muted}>{t("playbooksManage.loading")}</div>}

      {playbooks && sorted.length === 0 && (
        <div className={styles.empty}>{t("playbooksManage.empty")}</div>
      )}

      {sorted.length > 0 && (
        <div className={styles.list}>
          {sorted.map((pb) => {
            const busy = busyGuid === pb.playbookGuid;
            const confirming = confirmHideGuid === pb.playbookGuid;
            return (
              <div key={pb.playbookGuid} className={styles.row}>
                <div className={styles.main}>
                  <div className={styles.titleLine}>
                    <span className={styles.title}>{pb.title}</span>
                    <span className={pb.isActive ? styles.badgeListed : styles.badgeHidden}>
                      {pb.isActive ? t("playbooksManage.statusListed") : t("playbooksManage.statusHidden")}
                    </span>
                  </div>
                  <div className={styles.metaLine}>
                    {pb.categoryKey && <span className={styles.category}>{pb.categoryKey}</span>}
                    {authorName(pb.ownerUserGuid) && (
                      <span className={styles.author}>
                        {t("playbooksManage.by", { name: authorName(pb.ownerUserGuid) })}
                      </span>
                    )}
                  </div>
                </div>

                {confirming ? (
                  <div className={styles.confirm}>
                    <span className={styles.confirmText}>{t("masterPublish.hidePrompt")}</span>
                    <div className={styles.confirmActions}>
                      <button
                        className={styles.ghostBtn}
                        onClick={() => setConfirmHideGuid(null)}
                        disabled={busy}
                      >
                        {t("masterPublish.cancel")}
                      </button>
                      <button
                        className={styles.dangerBtn}
                        onClick={() => setActive(pb, false)}
                        disabled={busy}
                      >
                        {busy ? t("masterPublish.hiding") : t("masterPublish.hideConfirm")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className={styles.actions}>
                    {pb.masterMiniProjectGuid && (
                      <button
                        className={styles.linkBtn}
                        onClick={() => navigate(`/projects/${pb.masterMiniProjectGuid}/about`)}
                      >
                        {t("playbookCard.viewMaster")}
                      </button>
                    )}
                    {isAdmin && (
                      pb.isActive ? (
                        <button
                          className={styles.ghostBtn}
                          onClick={() => { setConfirmHideGuid(pb.playbookGuid); setError(null); }}
                          disabled={busy}
                        >
                          {t("playbooksManage.hide")}
                        </button>
                      ) : (
                        <button
                          className={styles.primaryBtn}
                          onClick={() => setActive(pb, true)}
                          disabled={busy}
                        >
                          {busy ? t("masterPublish.restoring") : t("playbooksManage.restore")}
                        </button>
                      )
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
